import React, { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import { useTranslation } from 'localization';
import { ItemManager } from 'types/channel';
import ButtonAtom from '../../../atoms/Button/ButtonAtom';
import './ItemManagerListMolecule.scss';

const DEBOUNCE_MS = 300;
const PAGE_SIZE = 20;

export interface ItemManagerListMoleculeProps {
  managers: ItemManager[];
  onAdd: (identityId: string) => Promise<void>;
  onRemove: (identityId: string) => Promise<void>;
  isMutating: boolean;
  className?: string;
}

const ItemManagerListMolecule: React.FC<ItemManagerListMoleculeProps> = ({
  managers,
  onAdd,
  onRemove,
  isMutating,
  className = '',
}) => {
  const { t } = useTranslation();

  const [searchInput, setSearchInput] = useState('');
  const [debouncedInput, setDebouncedInput] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }
    debounceRef.current = setTimeout(() => {
      setDebouncedInput(searchInput.trim());
    }, DEBOUNCE_MS);

    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, [searchInput]);

  const existingIds = useMemo(
    () => new Set(managers.map((m) => m.identityId)),
    [managers]
  );

  const isAlreadyAdded = debouncedInput.length > 0 && existingIds.has(debouncedInput);

  const handleSearchChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchInput(e.target.value);
    setError(null);
  }, []);

  const handleAdd = useCallback(async () => {
    if (!debouncedInput || isAlreadyAdded || isMutating) return;
    setError(null);
    try {
      await onAdd(debouncedInput);
      setSearchInput('');
      setDebouncedInput('');
    } catch {
      setError(t('common.error.unknown'));
    }
  }, [debouncedInput, isAlreadyAdded, isMutating, onAdd, t]);

  const handleRemove = useCallback(
    async (manager: ItemManager) => {
      const name = manager.displayName || manager.identityId;
      // eslint-disable-next-line no-alert
      if (!globalThis.confirm(t('marketplace.itemDetail.permissions.removeManagerConfirm', { name }))) {
        return;
      }
      setError(null);
      try {
        await onRemove(manager.identityId);
      } catch {
        setError(t('common.error.unknown'));
      }
    },
    [onRemove, t]
  );

  const handleLoadMore = useCallback(() => {
    setVisibleCount((c) => c + PAGE_SIZE);
  }, []);

  const visibleManagers = managers.slice(0, visibleCount);
  const hasMore = managers.length > visibleCount;

  const classes = ['marketplace-item-manager-list-molecule', className].filter(Boolean).join(' ');

  return (
    <section className={classes} data-testid="item-manager-list">
      <header className="marketplace-item-manager-list-molecule__header">
        <h4 className="marketplace-item-manager-list-molecule__title">
          {t('marketplace.itemDetail.permissions.managers')}
        </h4>
        <p className="marketplace-item-manager-list-molecule__hint">
          {t('marketplace.itemDetail.permissions.managersHint')}
        </p>
      </header>

      <div className="marketplace-item-manager-list-molecule__search">
        <label
          htmlFor="item-manager-search"
          className="marketplace-item-manager-list-molecule__label"
        >
          {t('marketplace.itemDetail.permissions.addManager')}
        </label>
        <div className="marketplace-item-manager-list-molecule__search-row">
          <input
            id="item-manager-search"
            type="text"
            className="marketplace-item-manager-list-molecule__input"
            value={searchInput}
            onChange={handleSearchChange}
            placeholder={t('marketplace.itemDetail.permissions.searchUsers')}
            disabled={isMutating}
            autoComplete="off"
          />
          <ButtonAtom
            type="button"
            variant="primary"
            shape="pill"
            size="small"
            label={t('marketplace.itemDetail.permissions.addManager')}
            onClick={handleAdd}
            disabled={!debouncedInput || isAlreadyAdded || isMutating}
            data-testid="item-manager-add-button"
          />
        </div>

      </div>

      {error && (
        <div className="marketplace-item-manager-list-molecule__error" role="alert">
          {error}
        </div>
      )}

      {managers.length === 0 ? (
        <p
          className="marketplace-item-manager-list-molecule__empty"
          data-testid="item-manager-empty"
        >
          {t('marketplace.itemDetail.permissions.noManagers')}
        </p>
      ) : (
        <ul className="marketplace-item-manager-list-molecule__list">
          {visibleManagers.map((manager) => {
            const name = manager.displayName || manager.identityId;
            return (
              <li
                key={manager.identityId}
                className="marketplace-item-manager-list-molecule__item"
              >
                <span className="marketplace-item-manager-list-molecule__item-name">
                  {name}
                </span>
                <ButtonAtom
                  type="button"
                  variant="text"
                  shape="pill"
                  size="small"
                  icon="pi pi-times"
                  label={t('marketplace.itemDetail.permissions.removeManager')}
                  onClick={() => handleRemove(manager)}
                  disabled={isMutating}
                  data-testid={`item-manager-remove-${manager.identityId}`}
                />
              </li>
            );
          })}
        </ul>
      )}

      {hasMore && (
        <div className="marketplace-item-manager-list-molecule__load-more">
          <ButtonAtom
            type="button"
            variant="text"
            shape="pill"
            size="small"
            label={t('common.loadMore')}
            onClick={handleLoadMore}
            data-testid="item-manager-load-more"
          />
        </div>
      )}
    </section>
  );
};

export default ItemManagerListMolecule;
